import React, { useEffect, useState } from 'react';
import { Card, Form, Select, Checkbox, Button, Table, Breadcrumb, Space } from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import { useSelector } from 'react-redux';

const { Option } = Select;

interface Student {
    student_id: number;
    student_name: string;
}

interface ClassItem {
    class_id: number;
    class_name: string;
}

const breadcrumbItems = [
    { title: 'Attendance' },
    { title: 'Mark Attendance' },
];

const Attendance = () => {
    const today = new Date().toISOString().split('T')[0];
    const user = useSelector((state: any) => state.user?.user);
    const [form] = Form.useForm();

    const [classes, setClasses] = useState<ClassItem[]>([]);
    const [students, setStudents] = useState<Student[]>([]);
    const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
    const [presentStudentIds, setPresentStudentIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        apiRequest<ClassItem[]>({ method: 'get', url: '/classes' })
            .then(setClasses)
            .catch(() => showError('Failed to load classes'));
    }, []);

    

    const fetchStudents = async (classId: number) => {
        try {
            setLoading(true);
            const data = await apiRequest<Student[]>({
                method: 'get',
                url: `/students?class_id=${classId}`
            });
            setStudents(data);
            setPresentStudentIds(data.map((s) => s.student_id)); // default all present
        } catch {
            showError('Failed to load students');
        } finally {
            setLoading(false);
        }
    };

    const handleClassChange = (classId: number) => {
        setSelectedClassId(classId);
        fetchStudents(classId);
    };

    const handleCheck = (studentId: number, checked: boolean) => {
        setPresentStudentIds((prev) =>
            checked ? [...prev, studentId] : prev.filter((id) => id !== studentId)
        );
    };

    const handleSubmit = async () => {
        if (!selectedClassId) return showError('Please select a class');

        try {
            await apiRequest({
                method: 'post',
                url: '/mark-student-attendance',
                data: {
                    class_id: selectedClassId,
                    date: new Date().toISOString().split('T')[0],
                    present_students: presentStudentIds,
                },
            });
            showSuccess('Attendance submitted successfully');
        } catch {
            showError('Failed to submit attendance');
        }
    };

    const columns = [
        {
            title: 'Student Name',
            dataIndex: 'student_name',
            key: 'student_name',
        },
        {
            title: 'Present',
            dataIndex: 'student_id',
            key: 'present',
            render: (student_id: number) => (
                <Checkbox
                    checked={presentStudentIds.includes(student_id)}
                    onChange={(e) => handleCheck(student_id, e.target.checked)}
                />
            ),
        },
    ];

    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: '16px',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb items={breadcrumbItems} />
            </Space>

            <Card title="Mark Student Attendance">
                <Form
                    form={form}
                    layout="inline"
                    style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }}
                >
                    <Form.Item
                        name="class_id"
                        rules={[{ required: true, message: 'Please select a class' }]}
                        style={{ minWidth: 250 }}
                    >
                        <Select
                            placeholder="Select Class"
                            onChange={handleClassChange}
                            style={{ width: 250, marginBottom:16 }}
                        >
                            {classes.map((cls) => (
                                <Option key={cls.class_id} value={cls.class_id}>
                                    {cls.class_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={handleSubmit} disabled={!students.length}>
                            Submit Attendance
                        </Button>
                    </Form.Item>
                </Form>

                <Table
                    dataSource={students.map((s) => ({ ...s, key: s.student_id }))}
                    columns={columns}
                    pagination={false}
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </>
    );
};

export default Attendance;
